/** Calendar endpoints — thin wrappers around the FastAPI backend. */

import type { EffectiveAccess, SharedByView } from "./notes";
import { ApiClient } from "./client";
import type { TagResponse } from "./tags";

export type { TagResponse, EffectiveAccess, SharedByView };

/** Caller-relative scope for ``GET /events``. ``"mine"`` returns only
 *  events the caller owns, ``"shared"`` only events shared *with* the
 *  caller, ``"all"`` (default) returns both. */
export type CalendarScope = "mine" | "shared" | "all";

/** Scope for updating or deleting a single occurrence of a recurring event. */
export type OccurrenceScope = "this" | "following" | "all";

export interface EventResponse {
  id: string;
  user_id: string;
  title: string;
  description: string;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  color: string;
  tags: TagResponse[];
  created_at: string;
  updated_at: string;
  location: string;
  /** Owner of the event — same as ``user_id`` for backwards compat,
   *  surfaced explicitly so the UI doesn't conflate "the API caller"
   *  with "the owner". */
  owner_id: string;
  /** Non-null on events shared *with* the caller. Null for events
   *  the caller owns. */
  shared_by: SharedByView | null;
  /** Effective access the caller has on this row. */
  my_access: EffectiveAccess;
  rrule: string | null;
  timezone: string | null;
  is_recurring: boolean;
  /** Canonical UTC identity of this occurrence; null for singles and masters. */
  occurrence_start: string | null;
  is_exception: boolean;
  next_occurrence_start: string | null;
  next_occurrence_at: string | null;
  /** The caller's own reminder offsets for this event (minutes; see reminderPresets). */
  reminders: number[];
}

export interface EventListResponse {
  events: EventResponse[];
}

export interface CreateEventPayload {
  title: string;
  description?: string;
  starts_at: string;
  ends_at: string;
  all_day?: boolean;
  color?: string;
  tags?: string[];
  location?: string;
  rrule?: string | null;
  timezone?: string;
  reminders?: number[];
}

export interface UpdateEventPayload {
  title?: string;
  description?: string;
  starts_at?: string;
  ends_at?: string;
  all_day?: boolean;
  color?: string;
  tags?: string[];
  location?: string;
  rrule?: string | null;
}

export interface UpdateOccurrencePayload extends Omit<UpdateEventPayload, "rrule"> {
  scope: OccurrenceScope;
  all_day?: boolean;
  rrule?: string | null;
}

export interface AgendaTaskResponse {
  id: string;
  list_id: string;
  title: string;
  due_date: string | null;
  start_date: string | null;
  completed: boolean;
  color: string;
  tags: TagResponse[];
}

export type HabitSlotState = "scheduled" | "partial" | "met" | "missed";

export interface AgendaHabitResponse {
  habit_id: string;
  name: string;
  color: string;
  /** ISO yyyy-mm-dd in the user's local tz. */
  date: string;
  target: number;
  count: number;
  reminder_time: string | null;
  state: HabitSlotState;
  tags: TagResponse[];
}

export interface AgendaResponse {
  start: string;
  end: string;
  events: EventResponse[];
  tasks: AgendaTaskResponse[];
  habits: AgendaHabitResponse[];
}

export const calendarApi = (client: ApiClient) => ({
  list(range: {
    from: string;
    to: string;
    q?: string;
    scope?: CalendarScope;
    expand?: boolean;
  }): Promise<EventListResponse> {
    const query: Record<string, string | undefined> = { from: range.from, to: range.to };
    if (range.q) query.q = range.q;
    if (range.scope !== undefined) query.scope = range.scope;
    if (range.expand === false) query.expand = "false";
    return client.request<EventListResponse>("/events", {
      method: "GET",
      query,
    });
  },

  get(eventId: string, opts: { occurrence?: string } = {}): Promise<EventResponse> {
    const query: Record<string, string | undefined> = {};
    if (opts.occurrence) query.occurrence = opts.occurrence;
    return client.request<EventResponse>(`/events/${eventId}`, { method: "GET", query });
  },
  agenda(range: {
    from: string;
    to: string;
    scope?: CalendarScope;
  }): Promise<AgendaResponse> {
    const query: Record<string, string | undefined> = {
      from: range.from,
      to: range.to,
    };
    if (range.scope !== undefined) query.scope = range.scope;
    return client.request<AgendaResponse>("/events/agenda", {
      method: "GET",
      query,
    });
  },
  create(payload: CreateEventPayload): Promise<EventResponse> {
    return client.request<EventResponse>("/events", { method: "POST", json: payload });
  },
  update(eventId: string, payload: UpdateEventPayload): Promise<EventResponse> {
    return client.request<EventResponse>(`/events/${eventId}`, {
      method: "PATCH",
      json: payload,
    });
  },
  delete(eventId: string): Promise<void> {
    return client.request<void>(`/events/${eventId}`, { method: "DELETE" });
  },
  updateOccurrence(
    eventId: string,
    occurrenceStart: string,
    payload: UpdateOccurrencePayload,
  ): Promise<EventResponse> {
    return client.request<EventResponse>(
      `/events/${eventId}/occurrences/${encodeURIComponent(occurrenceStart)}`,
      { method: "PATCH", json: payload },
    );
  },
  deleteOccurrence(eventId: string, occurrenceStart: string, scope: OccurrenceScope): Promise<void> {
    return client.request<void>(
      `/events/${eventId}/occurrences/${encodeURIComponent(occurrenceStart)}`,
      { method: "DELETE", query: { scope } },
    );
  },
  getReminders(eventId: string): Promise<{ offsets: number[] }> {
    return client.request<{ offsets: number[] }>(`/events/${eventId}/reminders`, { method: "GET" });
  },
  setReminders(eventId: string, offsets: number[]): Promise<{ offsets: number[] }> {
    return client.request<{ offsets: number[] }>(`/events/${eventId}/reminders`, {
      method: "PUT",
      json: { offsets },
    });
  },
});
